import { apiService } from 'core/services/apiService';
import { getTokenFromLocalStorage } from 'core/services/axiosInterceptors';
import { clearSessionCaches } from 'core/utils/clearSessionCaches';
import {
	getSessionExpiryMs,
	isSessionExpired,
	setByodAppSession,
	updateByodAppSession,
} from 'core/utils/byodAppSession';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { Role } from 'module/schedule/interfaces/ScheduleInterface';
import { createContext, useContext, useEffect, useState } from 'react';
import useUserDataStore from 'store/useUserDataStore';
interface ContextProps {
	user: any;
	login: any;
	error: any;
	isLoggedin: any;
	isWorkplaceSelected: any;
	setUserFromLocaleStorage: any;
	logout: any;
	role: Role[] | null;
	setRolesToLocal: any;
}

interface FormValues {
	email: string;
	password: string;
	tenant: string;
}

const authContext = createContext({} as ContextProps);

const MAX_TIMEOUT_MS = 2 ** 31 - 1;

function ProvideAuth({ children }: { children: React.ReactNode }): JSX.Element {
	const auth = useProvideAuth();
	return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

function useAuth(): ContextProps {
	return useContext(authContext);
}

function useProvideAuth() {
	const [user, setUser] = useState<any>(null);
	const [error, setError] = useState<string | null>(null);
	const [role, setRole] = useState<Role[] | null>(null);

	// Access the user data store
	const {
		fetchSecurityRoles,
		fetchHierarchyNodes,
		setCurrentUserRole,
		clearUserData,
		initializeFromLocalStorage,
	} = useUserDataStore();

	const login = async (data: FormValues) => {
		try {
			setError(null);
			// Convert email to lowercase before sending to API (with null check)
			const loginData = { ...data, email: data.email ? data.email.toLowerCase() : '' };
			const { tokenKey, expiresAt, usedBy } =
				apiService.responseHandler(await apiService.post('/token-web', loginData));

			// Drop stale UI caches from the previous session (auto-logout does not
			// always go through logout(), so week/date caches can otherwise survive).
			clearSessionCaches();

			// OPS-002 (remove path): do not persist unused refresh tokens in localStorage.
			setByodAppSession({
				token_key: tokenKey,
				expiresAt,
				usedBy,
				role: [],
				workplace: null,
			});

			const userDetails = apiService.responseHandler(await apiService.get(`/users/me`));
			// console.log('user details', userDetails);
			if (userDetails?.id) {
				localStorage.setItem('userId', userDetails.id.toString());
			}

			// If user has a security role in the /me response, store it
			if (userDetails?.securityRole) {
				setCurrentUserRole(userDetails.securityRole);
			}

			setUser(userDetails);

			setByodAppSession({
				user: userDetails,
				token_key: tokenKey,
				expiresAt,
				usedBy,
				role: [],
				workplace: null,
			});

			// Fetch security roles and hierarchy nodes after successful login
			try {
				// console.log('Fetching security roles and hierarchy nodes...');
				await Promise.all([fetchSecurityRoles(), fetchHierarchyNodes()]);
				// console.log('Security roles and hierarchy nodes fetched successfully');
			} catch (apiError) {
				console.error('Failed to fetch security roles or hierarchy nodes:', apiError);
				// Don't fail the login if these API calls fail
			}

			return true;
		} catch (error: any) {
			console.log('login error', error, typeof error);

			// Normalize error text from different shapes (string, Error-like, API response)
			const errorText: string =
				typeof error === 'string'
					? error
					: error && typeof error === 'object'
					? (error.message || error.error || JSON.stringify(error))
					: '';

			// If the server returned the specific access denied message, show the friendly message
			if (/access denied/i.test(errorText) || /invalid credentials/i.test(errorText) || /user deactivated/i.test(errorText)) {
				setError('Log In Failed, Please try again or reset your Password.');
			} else if (typeof error === 'string') {
				// pass through arbitrary string errors
				setError(error);
			} else if (error && typeof error === 'object' && typeof error.message === 'string') {
				setError(error.message);
			} else {
				setError('Log In Failed, Please try again or reset your Password.');
			}

			return false;
		}
	};

	const isLoggedin = () => {
		if (!getTokenFromLocalStorage('token_key')) {
			return false;
		}
		// Expired token: treat as logged out before any module mounts and fires
		// API calls with it (blank screen after idle + refresh).
		if (isSessionExpired()) {
			clearSessionCaches();
			return false;
		}
		return true;
	};

	const isWorkplaceSelected = () => {
		if (getTokenFromLocalStorage('workplace')) {
			return true;
		}
		return false;
	};

	const setUserFromLocaleStorage = () => {
		const user = getTokenFromLocalStorage('user');
		const role = getTokenFromLocalStorage('role');
		setUser(user);
		setRole(role);
	};

	const logout = async () => {
		clearUserData();
		try {
			const { default: useSettingsStore } = await import('store/useSettingsStore');
			useSettingsStore.getState().clearSettingsCache();
		} catch (e) {
			console.warn('Failed to clear settings cache on logout:', e);
		}
		clearSessionCaches();
		window.location.href = ROUTES.LOGIN;
	};



	const setRolesToLocal = (roles: Role[]) => {
		// DATA-001: write roles immediately via the shared session helper (no deferred RMW).
		updateByodAppSession({ role: roles });
		setRole(roles);
	};

	useEffect(() => {
		setUserFromLocaleStorage();
		// Initialize user data from localStorage
		initializeFromLocalStorage();
	}, [initializeFromLocalStorage]);

	// Send the user to login when the token expires, without waiting for a
	// refresh or a failing API call.
	useEffect(() => {
		if (!user) return;
		const expiresAtMs = getSessionExpiryMs();
		if (expiresAtMs === null) return;

		const delay = expiresAtMs - Date.now();
		// setTimeout overflows above ~24.8 days; skip, the refresh check covers it.
		if (delay > MAX_TIMEOUT_MS) return;

		const timer = window.setTimeout(() => {
			logout();
		}, Math.max(delay, 0));
		return () => window.clearTimeout(timer);
	}, [user]);
	// console.log("auth context user", user);
	return {
		user,
		login,
		error,
		isLoggedin,
		isWorkplaceSelected,
		setUserFromLocaleStorage,
		logout,
		setRolesToLocal,
		role,
	};
}

export { ProvideAuth, useAuth };
